import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from 'hooks/useAuth';
import { handleUnauthenticated, handleUnauthorised } from 'utils/authHelpers';
import { sendErrorToSentry } from 'utils/sentryHelpers';
import LoadingIndicator from 'ui/LoadingIndicator';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import {
  API_CONSUMER_VERSION,
  API_URL,
  CREDENTIALS,
  REQUEST_TIMEOUT
} from 'utils/constants';
import ActivityMonitor from './ActivityMonitor';

const ApiSetup = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect
  } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const history = useHistory();

  // Use references instead of state to don't re-render the tree on changes
  const requestInterceptorIdRef = useRef(null);
  const responseInterceptorIdRef = useRef(null);

  const setBaseDefaults = () => {
    axios.defaults.baseURL = API_URL;
    axios.defaults.headers.common['Api-Consumer-Version'] =
      API_CONSUMER_VERSION;
    axios.defaults.credentials = CREDENTIALS;
    axios.defaults.timeout = REQUEST_TIMEOUT;
  };

  const requestInterceptor = async (config) => {
    if (config.headers.Authorization) {
      return config;
    }
    try {
      // This will never renew the Access Token, only gets the one that is cached.
      // It is automatically refreshed in the ActivityMonitor.
      const token = await getAccessTokenSilently({
        cacheMode: 'cache-only'
      });
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      sendErrorToSentry(error);
      // The possible errors are:
      //  - login_required: When the session is invalid/expired and the cache is empty
      //     (or cache-only is used, and it's a miss).
      //  - consent_required: Scopes changed.
      //  - timeout: Request took too long (not applicable for cache-only).
      //  - missing_refresh_token: Configuration issue.
      //
      // If we reach this point, then the user DOES NEED authentication.
      // It is safe to cancel the request and redirect to the login page.
      handleUnauthenticated(loginWithRedirect, history);

      // Using axios.Cancel will cancel the request.
      // However, in the next version, this is deprecated.
      // When updating axios, we need to move to the standard AbortController.
      throw new axios.Cancel('Authentication required.');
    }
    return config;
  };

  const responseErrorInterceptor = (error) => {
    const status = error.response?.status;

    if (status === 401) {
      try {
        handleUnauthenticated(loginWithRedirect, history);
      } catch (callbackError) {
        sendErrorToSentry(callbackError);
      }
    }

    if (status === 403) {
      try {
        handleUnauthorised(history);
      } catch (callbackError) {
        sendErrorToSentry(callbackError);
      }
    }

    if (status === 404) {
      toastr.error('Not Found', 'The requested resource was not found.');
    }

    return Promise.reject(error);
  };

  const initializeAccessToken = async () => {
    try {
      await getAccessTokenSilently();
      setIsReady(true);
    } catch (error) {
      sendErrorToSentry(error);
      handleUnauthenticated(loginWithRedirect, history);
    }
  };

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      // If not authenticated (and not loading), no Axios setup needed.
      return;
    }

    setBaseDefaults();

    requestInterceptorIdRef.current =
      axios.interceptors.request.use(requestInterceptor);

    responseInterceptorIdRef.current = axios.interceptors.response.use(
      (response) => response,
      responseErrorInterceptor
    );

    initializeAccessToken();

    return () => {
      if (requestInterceptorIdRef.current !== null) {
        axios.interceptors.request.eject(requestInterceptorIdRef.current);
        requestInterceptorIdRef.current = null;
      }
      if (responseInterceptorIdRef.current !== null) {
        axios.interceptors.response.eject(responseInterceptorIdRef.current);
        responseInterceptorIdRef.current = null;
      }
    };
  }, [
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    history
  ]);

  if (isLoading || !isReady) {
    return <LoadingIndicator cover />;
  }

  return (
    <>
      {isAuthenticated && <ActivityMonitor />}
      {children}
    </>
  );
};

ApiSetup.propTypes = {
  children: PropTypes.node.isRequired
};

export default ApiSetup;
